// src/api/companyApi.js
import AuthService from "./axiosInstance";
import endpoints from "./endpoints";

const COMPANY_CACHE_TTL_MS = 5 * 60 * 1000;

const companyApi = {
  async getCompanies(params = {}) {
    const res = await AuthService.get(endpoints.COMPANIES, { params });
    return res.data;
  },

  async getCompany(slug) {
    if (!companyApi._companyCache) {
      companyApi._companyCache = new Map();
    }
    const cacheKey = String(slug || "");
    const cached = companyApi._companyCache.get(cacheKey);
    const now = Date.now();
    if (cached && now - cached.timestamp < COMPANY_CACHE_TTL_MS) {
      return cached.data;
    }
    const res = await AuthService.get(endpoints.COMPANY_DETAIL(slug));
    const data = res.data;
    companyApi._companyCache.set(cacheKey, { data, timestamp: now });
    return data;
  },

  async getCompanyFinancials(slug) {
    const res = await AuthService.get(endpoints.COMPANY_FINANCIALS(slug));
    return res.data;
  },

  async getCompanyManageOverview(slug, params = {}) {
    const res = await AuthService.get(endpoints.COMPANY_MANAGE_OVERVIEW(slug), {
      params,
    });
    return res.data;
  },

  async updateCompany(slug, payload) {
    const res = await AuthService.patch(endpoints.COMPANY_MANAGE_DETAIL(slug), payload);
    if (companyApi._companyCache) {
      companyApi._companyCache.delete(String(slug || ""));
    }
    return res.data;
  },

  async submitClaim({ companyId, companyEmail, relationshipDescription, proofUrl }) {
    const payload = {
      company: companyId,
      company_email: companyEmail,
      relationship_description: relationshipDescription,
      proof_url: proofUrl || null,
    };
    const res = await AuthService.post(endpoints.COMPANY_CLAIMS, payload);
    return res.data;
  },

  async getClaims(params = {}) {
    const res = await AuthService.get(endpoints.COMPANY_CLAIMS, { params });
    return res.data;
  },

  async submitCompanySubmission(companyId, options = {}) {
    const payload = {
      company: companyId,
      ...(options.preview ? { preview: true } : null),
      ...(options.replace ? { replace: true } : null),
      ...(options.cancel ? { cancel: true } : null),
    };
    const res = await AuthService.post(endpoints.COMPANY_SUBMISSION_SUBMIT, payload);
    return res.data;
  },

  async getAssets(params = {}) {
    const res = await AuthService.get(endpoints.ASSETS, { params });
    return res.data;
  },

  async createAsset(payload) {
    const res = await AuthService.post(endpoints.ASSETS, payload);
    return res.data;
  },

  async updateAsset(id, payload) {
    const res = await AuthService.patch(endpoints.ASSET_DETAIL(id), payload);
    return res.data;
  },

  async deleteAsset(id) {
    const res = await AuthService.delete(endpoints.ASSET_DETAIL(id));
    return res.data;
  },

  async getResourceReserves(params = {}) {
    const res = await AuthService.get(endpoints.RESOURCE_RESERVES, { params });
    return res.data;
  },

  async createResourceReserve(payload) {
    const res = await AuthService.post(endpoints.RESOURCE_RESERVES, payload);
    return res.data;
  },

  async updateResourceReserve(id, payload) {
    const res = await AuthService.patch(endpoints.RESOURCE_RESERVE_DETAIL(id), payload);
    return res.data;
  },

  async deleteResourceReserve(id) {
    const res = await AuthService.delete(endpoints.RESOURCE_RESERVE_DETAIL(id));
    return res.data;
  },

  async saveResourceReserveReportWithValues(reportPayload, values = [], reportId = null) {
    const res = await AuthService.post(
      reportId
        ? endpoints.RESOURCE_RESERVE_REPORT_SAVE_DETAIL(reportId)
        : endpoints.RESOURCE_RESERVE_REPORT_SAVE,
      { report: reportPayload, values }
    );
    return res.data;
  },

  async deleteResourceReserveReport(id) {
    const res = await AuthService.delete(endpoints.RESOURCE_RESERVE_REPORT_SAVE_DETAIL(id));
    return res.data;
  },

  async getResourceReserveEconomicScenarios(params = {}) {
    const res = await AuthService.get(endpoints.RESOURCE_RESERVE_ECONOMIC_SCENARIOS, { params });
    return res.data;
  },

  async createResourceReserveEconomicScenario(payload) {
    const res = await AuthService.post(endpoints.RESOURCE_RESERVE_ECONOMIC_SCENARIOS, payload);
    return res.data;
  },

  async updateResourceReserveEconomicScenario(id, payload) {
    const res = await AuthService.patch(
      endpoints.RESOURCE_RESERVE_ECONOMIC_SCENARIO_DETAIL(id),
      payload
    );
    return res.data;
  },

  async deleteResourceReserveEconomicScenario(id) {
    const res = await AuthService.delete(
      endpoints.RESOURCE_RESERVE_ECONOMIC_SCENARIO_DETAIL(id)
    );
    return res.data;
  },

  async saveProductionReportWithValues(reportPayload, values = [], reportId = null) {
    const res = await AuthService.post(
      reportId
        ? endpoints.PRODUCTION_REPORT_SAVE_DETAIL(reportId)
        : endpoints.PRODUCTION_REPORT_SAVE,
      { report: reportPayload, values }
    );
    return res.data;
  },

  async deleteProductionReport(id) {
    const res = await AuthService.delete(endpoints.PRODUCTION_REPORT_SAVE_DETAIL(id));
    return res.data;
  },

  async getProductionMetricDefinitions() {
    if (!companyApi._metricCache) {
      companyApi._metricCache = AuthService.get(endpoints.PRODUCTION_METRIC_DEFINITIONS).then(
        (res) => res.data
      );
    }
    return companyApi._metricCache;
  },

  async getCommodities() {
    if (!companyApi._commodityCache) {
      companyApi._commodityCache = AuthService.get(endpoints.COMMODITIES).then(
        (res) => res.data
      );
    }
    return companyApi._commodityCache;
  },

  async createPeriod(payload) {
    const res = await AuthService.post(endpoints.PERIODS, payload);
    return res.data;
  },
};

export default companyApi;
